<script lang="ts">
  import {
    cloneSalesActionProducts,
    createSalesAction,
    deleteSalesAction,
    patchSalesAction,
    restoreSalesActionStatus,
    salesActionListStore,
    updateSalesActionStatus,
  } from '@entities/sales-action/store.svelte';
  import CurrentActionTable from '@features/sales-action-inline-edit/CurrentActionTable.svelte';
  import ResultActionTable from '@features/sales-action-inline-edit/ResultActionTable.svelte';
  import { buildUnseenUpdateIds } from '@features/sales-action-inline-edit/unseen-update';
  import {
    getTabLastSeen,
    markTabSeen,
  } from '@shared/api/customer-tab-last-seen';
  import { sessionStore } from '@features/auth/session/model/store.svelte';
  import SalesActionEditModal from '@features/sales-action-edit/SalesActionEditModal.svelte';
  import { buildIssueTagOptions } from '@features/sales-action-edit/issue-tag-options';
  import WonConfirmDialog from '@features/sales-action-result-confirm/WonConfirmDialog.svelte';
  import LostReasonDialog from '@features/sales-action-result-confirm/LostReasonDialog.svelte';
  import DealTransitionDialog from '@features/sales-action-result-confirm/DealTransitionDialog.svelte';
  import AddSolutionDialog from '@features/sales-action-result-confirm/AddSolutionDialog.svelte';
  import { openSolutionIdsForAnchor } from '@features/sales-action-result-confirm/open-solutions';
  import {
    isCurrentAction,
    isResultAction,
  } from '@features/sales-action-inline-edit/tab-filter';
  import { planDealTransition } from '@features/sales-action-inline-edit/deal-transition';
  import {
    type ActionType,
    type LostReason,
    type SalesAction,
    type SalesActionListQuery,
    type SalesActionTarget,
    type SalesStatus,
  } from '@shared/types/sales-action';
  import type { Lawyer } from '@domain/lawyer/model';
  import { salesStatusRequiresProduct } from '@shared/constants/sales-action-display';
  import { customerProblemListStore } from '@entities/customer-problem/store.svelte';
  import { problemOptionsStore } from '@entities/problem/store.svelte';

  interface SolutionOption {
    id: string;
    label: string;
  }

  interface Props {
    target: SalesActionTarget;
    canEdit: boolean;
    /// All sales-able solutions (`products`). Surfaced as a picker in the
    /// create modal and used to render the (read-only) solution column.
    solutions: SolutionOption[];
    /// その事務所の所属弁護士。 対象弁護士 (catalog No.116) の選択候補 +
    /// 一覧の対象弁護士列の弁護士名解決に使う。
    lawyers?: Lawyer[];
    /// 受注時に「契約を作成する」が選ばれた、 または結果タブの契約未作成行で
    /// 「契約を作成する」CTA が押されたら親へ通知する。 親は自身の
    /// ContractCreateModal を開き、 作成成功後に作成元 action の
    /// linked_contract_id を PATCH する。 未指定なら受注確定のみ行う。
    onCreateContract?: (action: SalesAction, solutionId: string | null) => void;
    /// 契約未作成行と受注確定モーダルに「契約を作成する」CTA を出すか。
    /// 契約作成成功後に linked_contract_id を PATCH する親 (法律事務所詳細・弁護士
    /// 詳細の両方、 86eya0dva PR4 で共通化済み) だけが有効化する。
    resultCreateContractCta?: boolean;
  }

  let {
    target,
    canEdit,
    solutions,
    lawyers = [],
    onCreateContract,
    resultCreateContractCta = false,
  }: Props = $props();

  // 行ごとの課題表示 / 選択肢に使う customer_problems と課題マスタ名。
  // データ取得は entity store へ委譲し、component は表示専念。
  $effect(() => {
    const query =
      target.kind === 'firm'
        ? { firm_id: target.id, include_rejected: true as const, page_size: 200 }
        : { lawyer_id: target.id, include_rejected: true as const, page_size: 200 };
    void customerProblemListStore.refresh(query);
    // 課題マスタ全件 (id→name 解決用) は problemOptionsStore が担う。
    // 既にロード済みであれば再 fetch しない (lazy guard)。
    if (problemOptionsStore.data.length === 0 && !problemOptionsStore.loading) {
      void problemOptionsStore.load();
    }
  });

  const customerProblems = $derived(
    customerProblemListStore.data?.items ?? [],
  );
  /// 課題マスタを id→name Map に変換。problemOptionsStore は全件保持するので
  /// ここでは変換のみ。200 件超えの取りこぼしも store.load() が一括処理する。
  const problemNameById = $derived(
    new Map(problemOptionsStore.data.map((p) => [p.id, p.name])),
  );

  // セレクトの通常候補 (画面ターゲットの課題)。行の課題表示 (課題名/AIスコア/
  // 確認状態) は一覧応答同梱の source_problem を正とし、ここには依存しない
  // (86eya0dva PR3 — 混在行の参照課題は画面ターゲットの候補外にあり得る)。
  const issueTagOptions = $derived(
    buildIssueTagOptions(customerProblems, problemNameById),
  );

  // ── タブ: 現在の次アクション / 結果 ──────────────────────────────────
  type Tab = 'current' | 'result';
  let tab = $state<Tab>('current');

  $effect(() => {
    void refresh();
  });

  async function refresh() {
    const query: SalesActionListQuery = {
      target_kind: target.kind,
      ...(target.kind === 'firm'
        ? { firm_id: target.id }
        : { lawyer_id: target.id }),
      page_size: 200,
      sort_by: 'due_date',
      sort_dir: 'asc',
    };
    await salesActionListStore.refresh(query);
  }

  const allItems = $derived(salesActionListStore.data?.items ?? []);
  const currentItems = $derived(allItems.filter(isCurrentAction));
  const resultItems = $derived(allItems.filter(isResultAction));
  const items = $derived(tab === 'current' ? currentItems : resultItems);
  // 現在の次アクションのうち要確認の件数 (Issue #524 PR2)。 件数 (情報量) とは別意味の
  // 行動判断表示。 タブをまたいで残り、確認済み・編集・完了/受注/失注/取下げで消える。
  const currentReviewRequiredCount = $derived(
    currentItems.filter((a) => a.review_required).length,
  );

  // ── 営業アクションタブの閲覧状態 / 「更新」導出 (Issue #524 PR3) ─────────
  // 「更新」 = 他ユーザーが自分の last_seen 以降に追加・編集した現在アクション。
  // タブを開いた時点の last_seen を基準に固定して導出し、 その後 last_seen を now で
  // 更新する。 こうすると今表示中の「更新」は消えず (基準を固定)、 次にタブを開くと
  // 消える (ユーザー別の閲覧状態 / tab_key=sales_actions のみ利用)。
  let lastSeenBaseline = $state<string | null>(null);
  let lastSeenReady = $state(false);

  $effect(() => {
    const kind = target.kind;
    const id = target.id;
    lastSeenReady = false;
    let cancelled = false;
    void (async () => {
      try {
        const seen = await getTabLastSeen({
          target_kind: kind,
          target_id: id,
          tab_key: 'sales_actions',
        });
        if (cancelled) return;
        lastSeenBaseline = seen.last_seen_at;
        lastSeenReady = true;
      } catch {
        // 閲覧状態を取得できないときは「更新」表示を諦める (致命ではない)。
        return;
      }
      // 基準確定後に「見た」を記録する (失敗しても今の表示は維持)。
      try {
        await markTabSeen({
          target_kind: kind,
          target_id: id,
          tab_key: 'sales_actions',
        });
      } catch {
        // 記録失敗は次回タブを開いたときにリトライされる。
      }
    })();
    return () => {
      cancelled = true;
    };
  });

  // 「更新」判定の自分除外は actor (app_accounts.id) で行う (#578)。 last_seen ブックマークは
  // sub keying だが、 変更者比較は account_id (JIT 同期で解決済みの sessionStore.accountId)。
  const currentUserAccountId = $derived(sessionStore.accountId ?? null);
  // 「更新」に該当する現在アクションの id 集合。 基準 (last_seen) が確定するまでは空集合
  // (初回描画で他者行が一瞬出る/消えるのを防ぐ)。
  const unseenUpdateIds = $derived(
    lastSeenReady
      ? buildUnseenUpdateIds(currentItems, lastSeenBaseline, currentUserAccountId)
      : new Set<string>(),
  );

  // ── 作成モーダル (新規行のみ。 ソリューション/初期ステータスは作成時に設定) ──
  let createOpen = $state(false);
  function openCreate() {
    createOpen = true;
  }
  function handleCreated() {
    createOpen = false;
    void refresh();
  }

  // ── インライン保存 ───────────────────────────────────────────────
  // 1 行ずつ保存し、 成否を返す。 finally で必ず refresh するため、 失敗時は
  // サーバの旧値で再描画され、 変更が自動的に元へ戻る (= ロールバック)。
  let savingId = $state<string | null>(null);
  let rowErrors = $state<Record<string, string | null>>({});

  async function runSave(
    action: SalesAction,
    fn: () => Promise<unknown>,
  ): Promise<boolean> {
    if (!canEdit) return false;
    savingId = action.id;
    rowErrors = { ...rowErrors, [action.id]: null };
    let ok = false;
    try {
      await fn();
      ok = true;
    } catch (err) {
      rowErrors = {
        ...rowErrors,
        [action.id]: err instanceof Error ? err.message : String(err),
      };
    } finally {
      savingId = null;
      await refresh();
    }
    return ok;
  }

  // クライアント側の不変条件違反 (商談以降の product 未設定など) を、 API を呼ばずに
  // 行エラーとして出し、 インライン select をサーバ状態へ戻す (runSave 失敗時と同じ表示)。
  function blockRow(action: SalesAction, message: string) {
    rowErrors = { ...rowErrors, [action.id]: message };
    void refresh();
  }

  function saveDueDate(action: SalesAction, value: string) {
    const due = value === '' ? null : value;
    if (due === (action.due_date ?? null)) return;
    void runSave(action, () => patchSalesAction(action.id, { due_date: due }));
  }

  function saveActionType(action: SalesAction, value: string) {
    const next = value === '' ? null : (value as ActionType);
    if (next === (action.action_type ?? null)) return;
    void runSave(action, () =>
      patchSalesAction(action.id, { action_type: next }),
    );
  }

  function saveActionTitle(action: SalesAction, value: string) {
    const title = value.trim() === '' ? null : value.trim();
    if (title === (action.action_title ?? null)) return;
    void runSave(action, () =>
      patchSalesAction(action.id, { action_title: title }),
    );
  }

  function saveIssueTag(action: SalesAction, value: string) {
    const next = value === '' ? null : value;
    if (next === (action.source_customer_problem_id ?? null)) return;
    void runSave(action, () =>
      patchSalesAction(action.id, { source_customer_problem_id: next }),
    );
  }

  function saveNote(action: SalesAction, value: string) {
    const note = value.trim() === '' ? null : value.trim();
    if (note === (action.note ?? null)) return;
    void runSave(action, () => patchSalesAction(action.id, { note }));
  }

  function toggleParticipant(action: SalesAction, lawyerId: string) {
    const current = action.participant_lawyer_ids;
    const next = current.includes(lawyerId)
      ? current.filter((x) => x !== lawyerId)
      : [...current, lawyerId];
    void runSave(action, () =>
      patchSalesAction(action.id, { participant_lawyer_ids: next }),
    );
  }

  // 商談前の候補ソリューション (candidate_product_ids) を 1 件トグルして置換 PATCH。
  // product_id (案件ソリューション) は locked のためここでは触らない。
  function toggleCandidate(action: SalesAction, solutionId: string) {
    const current = action.candidate_product_ids;
    const next = current.includes(solutionId)
      ? current.filter((x) => x !== solutionId)
      : [...current, solutionId];
    void runSave(action, () =>
      patchSalesAction(action.id, { candidate_product_ids: next }),
    );
  }

  // ── 顧客ステータス変更 (= /complete) + won/lost + 商談化フロー ───────────
  let wonTarget = $state<SalesAction | undefined>(undefined);
  let lostTarget = $state<SalesAction | undefined>(undefined);
  // 商談前 → 商談以降 (negotiation_*) の確認モーダル対象。 候補なし / 2 件以上で開く。
  let dealTransitionTarget = $state<
    { action: SalesAction; targetStatus: SalesStatus } | undefined
  >(undefined);
  // 「ソリューションを追加」 対象 (商談以降 open 行)。
  let addSolutionTarget = $state<SalesAction | undefined>(undefined);
  // 既に open 行があるソリューション (モーダルで disabled 表示)。 判定は
  // **対象行の保存アンカー基準** (86eya0dva PR3): 一覧には firm アンカー行と
  // 弁護士アンカー行が混在し、 同一商品の混在は正当な並存 (open 一意性は
  // アンカー単位) なので、 表示行全体の商品集約では偽陽性ブロックになる。
  const dealTransitionOpenIds = $derived(
    dealTransitionTarget
      ? openSolutionIdsForAnchor(allItems, dealTransitionTarget.action.target)
      : [],
  );
  const addSolutionOpenIds = $derived(
    addSolutionTarget
      ? openSolutionIdsForAnchor(allItems, addSolutionTarget.target)
      : [],
  );

  const PRE_NEGOTIATION_STATUSES: SalesStatus[] = [
    'no_contact',
    'contacted',
    'in_dialogue',
    'appointment',
  ];
  const NEGOTIATION_STATUSES: SalesStatus[] = [
    'negotiation_c',
    'negotiation_b',
    'negotiation_a',
  ];
  const isPre = (s: SalesStatus) => PRE_NEGOTIATION_STATUSES.includes(s);

  function onStatusChange(action: SalesAction, value: string) {
    const next = value as SalesStatus;
    if (next === action.sales_status) return;
    if (next === 'won') {
      wonTarget = action;
      return;
    }
    if (next === 'lost') {
      lostTarget = action;
      return;
    }

    // 商談前 → 商談以降 (negotiation_*): ソリューション必須。候補を黙って捨てない。
    if (isPre(action.sales_status) && NEGOTIATION_STATUSES.includes(next)) {
      if (action.product_id) {
        // 既に案件ソリューションがあるなら保持して昇格。
        void promoteToDeal(action, next, [action.product_id]);
        return;
      }
      const candidates = action.candidate_product_ids;
      if (candidates.length === 1) {
        // 候補 1 件 → そのまま product_id に昇格 (確認不要)。
        void promoteToDeal(action, next, candidates);
        return;
      }
      // 候補なし or 2 件以上 → 確認モーダル (単一選択 / 行分割)。
      dealTransitionTarget = { action, targetStatus: next };
      return;
    }

    // 商談以降 → 商談前へ戻す: 既存 product_id を候補として保持 (黙って捨てない)。
    if (!isPre(action.sales_status) && isPre(next)) {
      void demoteToPre(action, next);
      return;
    }

    // それ以外 (商談前↔商談前 / 商談以降↔商談以降): product_id は現状維持で status 更新。
    void runSave(action, () =>
      updateSalesActionStatus(action.id, { sales_status: next }),
    );
  }

  /// 商談前行を商談以降へ昇格する。 solutionIds[0] を元行に設定し、 残りは
  /// 「1ソリューション1行」 の新規商談行として作成する (行分割)。 候補は商談行に残さない。
  async function promoteToDeal(
    action: SalesAction,
    targetStatus: SalesStatus,
    solutionIds: string[],
  ) {
    const first = solutionIds[0];
    if (!canEdit || first === undefined) return;
    // 分割新規行は元行の保存アンカーを引き継ぐ (画面ターゲットではない)。
    const plan = planDealTransition(action, targetStatus, solutionIds);
    savingId = action.id;
    rowErrors = { ...rowErrors, [action.id]: null };
    try {
      await updateSalesActionStatus(action.id, {
        sales_status: plan.promote.salesStatus,
        product_id: plan.promote.solutionId,
      });
      if ((action.action_type ?? null) !== plan.promote.actionType) {
        await patchSalesAction(action.id, {
          action_type: plan.promote.actionType,
        });
      }
      if (plan.clearCandidates) {
        await patchSalesAction(action.id, { candidate_product_ids: [] });
      }
      // 残りのソリューションは同じ商談条件で別行を作成。既存 open は選択不可。
      for (const body of plan.creates) {
        await createSalesAction(body);
      }
    } catch (err) {
      rowErrors = {
        ...rowErrors,
        [action.id]: err instanceof Error ? err.message : String(err),
      };
    } finally {
      savingId = null;
      await refresh();
    }
  }

  /// 商談以降行を商談前へ戻す。 既存 product_id を候補へ移してから solution を解除する
  /// (順序を逆にすると遷移失敗時に solution を失うため、 先に候補へ退避する)。
  async function demoteToPre(action: SalesAction, targetStatus: SalesStatus) {
    if (!canEdit) return;
    const oldSolution = action.product_id;
    savingId = action.id;
    rowErrors = { ...rowErrors, [action.id]: null };
    try {
      if (oldSolution && !action.candidate_product_ids.includes(oldSolution)) {
        await patchSalesAction(action.id, {
          candidate_product_ids: [
            ...action.candidate_product_ids,
            oldSolution,
          ],
        });
      }
      await updateSalesActionStatus(action.id, {
        sales_status: targetStatus,
        product_id: null,
      });
    } catch (err) {
      rowErrors = {
        ...rowErrors,
        [action.id]: err instanceof Error ? err.message : String(err),
      };
    } finally {
      savingId = null;
      await refresh();
    }
  }

  async function confirmDealTransition(solutionIds: string[]) {
    const t = dealTransitionTarget;
    dealTransitionTarget = undefined;
    if (!t) return;
    await promoteToDeal(t.action, t.targetStatus, solutionIds);
  }

  async function cancelDealTransition() {
    dealTransitionTarget = undefined;
    await refresh(); // インライン select を元のステータスへ戻す
  }

  function openAddSolution(action: SalesAction) {
    addSolutionTarget = action;
  }

  async function confirmAddSolution(solutionIds: string[], copyNote: boolean) {
    const action = addSolutionTarget;
    addSolutionTarget = undefined;
    if (!action) return;
    savingId = action.id;
    rowErrors = { ...rowErrors, [action.id]: null };
    try {
      await cloneSalesActionProducts(action.id, {
        product_ids: solutionIds,
        copy_note: copyNote,
      });
    } catch (err) {
      rowErrors = {
        ...rowErrors,
        [action.id]: err instanceof Error ? err.message : String(err),
      };
    } finally {
      savingId = null;
      await refresh();
    }
  }

  function cancelAddSolution() {
    addSolutionTarget = undefined;
  }

  // ソリューションのインライン編集。 PATCH /status は product_id を
  // omitted=保持 / null=解除 / string=設定 で扱う。 sales_status は現状維持で送る
  // (現在の次アクションは非終端なので open のまま)。
  function saveSolution(action: SalesAction, value: string) {
    const next = value === '' ? null : value;
    if (next === (action.product_id ?? null)) return;
    // 商談以降は product 必須なので「未設定」へのクリアをブロック。
    if (next === null && salesStatusRequiresProduct(action.sales_status)) {
      blockRow(action, '商談以降はソリューションが必須です。');
      return;
    }
    void runSave(action, () =>
      updateSalesActionStatus(action.id, {
        sales_status: action.sales_status,
        product_id: next,
      }),
    );
  }

  async function confirmWon(createContract: boolean, solutionId: string) {
    const action = wonTarget;
    wonTarget = undefined;
    if (!action) return;
    const ok = await runSave(action, () =>
      updateSalesActionStatus(action.id, {
        sales_status: 'won',
        product_id: solutionId,
      }),
    );
    if (!ok) return;
    // 受注行は結果タブへ移る。 「あとで作成」でも行が現在タブから消えるだけに
    // ならないよう、 結果タブへ切り替えて契約未作成バッジを見せる。
    tab = 'result';
    if (createContract && resultCreateContractCta) {
      onCreateContract?.(action, solutionId);
    }
  }

  // 結果タブの契約未作成行からの契約作成 CTA。 受注行の確定済みソリューション
  // (product_id) をそのまま渡す。
  function requestCreateContract(action: SalesAction) {
    onCreateContract?.(action, action.product_id);
  }

  async function cancelWon() {
    wonTarget = undefined;
    await refresh(); // インライン select を元のステータスへ戻す
  }

  async function confirmLost(
    lostReason: LostReason,
    lostReasonNote: string,
    solutionId: string,
  ) {
    const action = lostTarget;
    lostTarget = undefined;
    if (!action) return;
    await runSave(action, () =>
      updateSalesActionStatus(action.id, {
        sales_status: 'lost',
        product_id: solutionId,
        lost_reason: lostReason,
        lost_reason_note: lostReasonNote,
      }),
    );
  }

  async function cancelLost() {
    lostTarget = undefined;
    await refresh();
  }

  // ── ステータスを戻す (結果タブの誤操作救済) ─────────────────────────
  // 結果行 (completed + won/lost) を直前の非結果ステータスで現在タブへ戻す。
  // 確認ダイアログは ResultActionTable 側が出し、 確定後にここが呼ばれる。
  async function restoreStatus(action: SalesAction) {
    const ok = await runSave(action, () => restoreSalesActionStatus(action.id));
    // 成功時のみ現在タブへ切り替える (対象行が現在タブに現れる)。 失敗時は
    // runSave が行エラー表示 + refresh でサーバ値へ戻すため、 タブは維持する。
    if (ok) tab = 'current';
  }

  // ── 行末「…」メニュー: 取下げ (= DELETE) ─────────────────────────────
  let openMenuId = $state<string | null>(null);
  function closeMenus() {
    openMenuId = null;
  }
  function setOpenMenuId(id: string | null) {
    openMenuId = id;
  }
  // ポップオーバー外をクリックしたら閉じる。 内側 (`.popover`) のクリックでは
  // 閉じないので、 対象弁護士のチェックボックスは続けてトグルできる。
  function closeMenusOnOutsideClick(ev: MouseEvent) {
    const target = ev.target as HTMLElement | null;
    if (target?.closest('.popover')) return;
    closeMenus();
  }

  async function withdraw(action: SalesAction) {
    openMenuId = null;
    if (!canEdit) return;
    if (!confirm(`「${action.action_title ?? '(無題)'}」を取り下げますか?`))
      return;
    await runSave(action, () => deleteSalesAction(action.id));
  }
</script>

<svelte:window onclick={closeMenusOnOutsideClick} />

<section class="panel">
  <header class="panel-header">
    <div class="panel-titles">
      <h2 data-testid="sales-action-panel-title">提案・アクション一覧</h2>
      <p class="subtitle muted small">
        現在の次アクションは一覧上で直接更新できます。受注/失注だけが結果に移ります。
      </p>
    </div>
    <button
      type="button"
      class="primary"
      disabled={!canEdit}
      onclick={openCreate}
    >
      アクションを追加
    </button>
  </header>

  <div class="filter-row" role="tablist" aria-label="表示切替">
    <button
      type="button"
      class="chip"
      class:active={tab === 'current'}
      onclick={() => (tab = 'current')}
      role="tab"
      aria-selected={tab === 'current'}
      data-testid="tab-current"
    >
      現在の次アクション ({currentItems.length})
      {#if currentReviewRequiredCount > 0}
        <span
          class="review-flag"
          data-testid="current-review-required"
          aria-label={`要確認 ${currentReviewRequiredCount}件`}
        >
          要確認
        </span>
      {/if}
    </button>
    <button
      type="button"
      class="chip"
      class:active={tab === 'result'}
      onclick={() => (tab = 'result')}
      role="tab"
      aria-selected={tab === 'result'}
      data-testid="tab-result"
    >
      結果 ({resultItems.length})
    </button>
  </div>

  {#if salesActionListStore.loading && !salesActionListStore.data}
    <p class="status">読み込み中…</p>
  {:else if salesActionListStore.error}
    <p class="status error">{salesActionListStore.error}</p>
  {:else if items.length === 0}
    <p class="status">
      {#if tab === 'current'}
        現在の次アクションはありません。「アクションを追加」で作成できます。
      {:else}
        結果 (受注/失注) はまだありません。
      {/if}
    </p>
  {:else if tab === 'current'}
    <CurrentActionTable
      {items}
      {canEdit}
      {savingId}
      {rowErrors}
      {lawyers}
      {solutions}
      {issueTagOptions}
      {unseenUpdateIds}
      {openMenuId}
      {setOpenMenuId}
      {saveDueDate}
      {saveActionType}
      {saveActionTitle}
      {saveIssueTag}
      {saveNote}
      {toggleParticipant}
      {onStatusChange}
      {saveSolution}
      {toggleCandidate}
      addSolution={openAddSolution}
      {withdraw}
    />
  {:else}
    <ResultActionTable
      {items}
      {lawyers}
      {solutions}
      {canEdit}
      {savingId}
      {rowErrors}
      {restoreStatus}
      onCreateContract={resultCreateContractCta && onCreateContract
        ? requestCreateContract
        : undefined}
    />
  {/if}
</section>

<SalesActionEditModal
  open={createOpen}
  {target}
  existing={undefined}
  solutionOptions={solutions}
  {lawyers}
  onClose={() => (createOpen = false)}
  onSaved={handleCreated}
/>

{#if wonTarget}
  <WonConfirmDialog
    open={true}
    action={wonTarget}
    {solutions}
    showCreateContract={resultCreateContractCta && Boolean(onCreateContract)}
    onConfirm={confirmWon}
    onClose={cancelWon}
  />
{/if}

{#if lostTarget}
  <LostReasonDialog
    open={true}
    action={lostTarget}
    {solutions}
    onConfirm={confirmLost}
    onClose={cancelLost}
  />
{/if}

{#if dealTransitionTarget}
  <DealTransitionDialog
    open={true}
    action={dealTransitionTarget.action}
    targetStatus={dealTransitionTarget.targetStatus}
    {solutions}
    existingOpenSolutionIds={dealTransitionOpenIds}
    onConfirm={confirmDealTransition}
    onClose={cancelDealTransition}
  />
{/if}

{#if addSolutionTarget}
  <AddSolutionDialog
    open={true}
    action={addSolutionTarget}
    {solutions}
    existingOpenSolutionIds={addSolutionOpenIds}
    onConfirm={confirmAddSolution}
    onClose={cancelAddSolution}
  />
{/if}

<style>
  .panel {
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
  }
  .panel-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    min-width: 0;
  }
  .panel-titles {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }
  .panel-titles h2 {
    margin: 0;
    font-size: 1rem;
    color: #111827;
  }
  .subtitle {
    margin: 0;
  }
  .filter-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    min-width: 0;
  }
  .chip {
    padding: 3px 12px;
    border: 1px solid #d1d5db;
    background: #fff;
    color: #374151;
    border-radius: 999px;
    cursor: pointer;
    font-size: 0.75rem;
    font: inherit;
  }
  .chip.active {
    background: #1f2937;
    border-color: #1f2937;
    color: white;
  }
  /* 要確認フラグ (Issue #524 PR2)。 件数 (情報量) とは別の行動判断表示なので、
     active/非 active を問わず注意色で出す。 */
  .review-flag {
    margin-left: 4px;
    padding: 0 6px;
    border-radius: 999px;
    background: #fef3c7;
    color: #b45309;
    border: 1px solid #fcd34d;
    font-size: 0.6875rem;
    font-weight: 600;
  }
  .status {
    color: #6b7280;
    margin: 0;
  }
  .status.error {
    color: #b91c1c;
  }
  .muted {
    color: #6b7280;
  }
  .small {
    font-size: 0.75rem;
  }
  button.primary {
    padding: 8px 16px;
    border: 1px solid #2563eb;
    background: #2563eb;
    color: white;
    border-radius: 4px;
    cursor: pointer;
    font: inherit;
  }
  button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
